(function () {
    'use strict';

    angular
        .module('app')
        .factory('DialogService', DialogService);

    DialogService.$inject = ['$rootScope', '$uibModal', '$log', '$q'];

    /**
     * @ngdoc service
     * @name DialogService
     * 
     * @description
     * Shows a modal dialog.
     */
    function DialogService($rootScope, $uibModal, $log, $q) {
        var service = {
            alert: alert,
            alertHtml: alertHtml,
            confirm: confirm,
            confirmWithTitle: confirmWithTitle,
            confirmHtml: confirmHtml,
            unsavedChanges: unsavedChanges,
            messageList: messageList,
            error: error,
            show: show,
            sizes: {
                extraLargeFixedHeight: 'xl-fh',
                extraLarge: 'xl',
                large: 'lg',
                small: 'sm'
            }
        };

        return service;

        /**
         * @param {string} alertText
         *
         * @returns {promise} A promise that resolves when the user confirms the dialog.
         */
        function alert(alertText) {
            return showDefaultUibDialog('OK', '', '', alertText);
        }

        /**
         * @param {string} alertHtml
         *
         * @returns {promise} A promise that resolves when the user confirms the dialog.
         */
        function alertHtml(alertHtml) {
            return showDefaultUibDialog('OK', '', '', alertHtml);
        }

        /**
         * @param {string} confirmText
         * @param {string} ariaLabel
         * @param {string} okText - Optional, text for the "OK" button
         * @param {string} cancelText - Optional, text for the "Cancel" button
         *
         * @returns {promise} A promise that resolves if the user confirms, otherwise rejects.
         */
        function confirm(confirmText, ariaLabel, okText, cancelText) {
            return showDefaultUibDialog(okText || 'OK', cancelText || 'Cancel', '', confirmText);
        }

        /**
         * @param {string} confirmTitle
         * @param {string} confirmText
         * @param {string} ariaLabel
         * @param {string} okText - Optional, text for the "OK" button
         * @param {string} cancelText - Optional, text for the "Cancel" button
         *
         * @returns {promise} A promise that resolves if the user confirms, otherwise rejects.
         */
        function confirmWithTitle(confirmTitle, confirmText, ariaLabel, okText, cancelText) {
            return showDefaultUibDialog(okText || 'OK', cancelText || 'Cancel', confirmTitle, confirmText);
        }


        /**
         * @param {string} confirmHtmlText - HTML formatted text for the confirmation dialog
         * @param {string} ariaLabel
         * @param {string} okText - Optional, text for the "OK" button
         * @param {string} cancelText - Optional, text for the "Cancel" button
         *
         * @returns {promise} A promise that resolves if the user confirms, otherwise rejects.
         */
        function confirmHtml(confirmHtmlText, ariaLabel, okText, cancelText) {
            return showDefaultUibDialog(okText || 'OK', cancelText || 'Cancel', '', confirmHtmlText);
        }

        /**
         * @param {string} name - Name of the item with unsaved changes.
         * @param {bool} threeOptions - If true, dialog shows Cancel, No, Yes. If false, dialog shows Cancel, OK.
         * @param {string} body - HTML formatted text. Overrides the default body template. Optional.
         *
         * @returns {promise} A promise that resolves if the user selects yes/no with the values true and false respectively, otherwise rejects.
         */
        function unsavedChanges(name, threeOptions, body) {
            return show({
                locals: { name: name, threeOptions: threeOptions, body: body },
                controller: 'UnsavedChangesDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/controls/dialog/unsaved-changes-dialog.tmpl.html'
            });
        }

        /**
         * Display list of messages using a dialog.
         * The dialog will contain an 'error' icon in the title bar
         *
         * @param {string} title - Title text.
         * @param {array} messages - List of message strings.
         * @param {array} error - List of html form validation errors. These will be added to the list of messages to be shown.
         */
        function messageList(title, messages, error) {
            return show({
                locals: { title: title, messages: messages, error: error },
                controller: 'MessageListDialogCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/controls/dialog/message-list-dialog.tmpl.html'
            });
        }

        /**
         * Display an error dialog to the user, AND log the error.
         *
         * @param {string} errorText - The text to be displayed in the dialog.
         * @param {Object|string} httpServiceResponse - A response object from $http service, or a string.
         *   If the error is the result of an $http service call, provide the response to help construct the error
         *   text on the basis of that response.
         *
         * @returns {promise} A promise that resolves when the user confirms the dialog.
         */
        function error(errorText, httpServiceResponse) {
            var responseText = null;
            if (angular.isDefined(httpServiceResponse)) {
                if (angular.isObject(httpServiceResponse) && httpServiceResponse.data && httpServiceResponse.data.Message) {
                    responseText = httpServiceResponse.data.Message;
                } else if (angular.isString(httpServiceResponse)) {
                    responseText = httpServiceResponse;
                }
            }

            try {
                var appInsightsText;
                if (errorText && responseText) {
                    appInsightsText = errorText + ' - ' + responseText;
                } else if (errorText) {
                    appInsightsText = errorText;
                } else {
                    appInsightsText = 'Unspecified error.';
                }

                var appInsightsProperties = {};
                if (angular.isDefined(httpServiceResponse) && angular.isObject(httpServiceResponse)) {
                    if (angular.isDefined(httpServiceResponse.config) && angular.isObject(httpServiceResponse.config)) {
                        appInsightsProperties.method = httpServiceResponse.config.method;
                        appInsightsProperties.url = httpServiceResponse.config.url;
                    }
                    appInsightsProperties.status = httpServiceResponse.status;
                    appInsightsProperties.statusText = httpServiceResponse.statusText;
                }

                appInsights.trackException(appInsightsText, "unhandled", appInsightsProperties);
            } catch (ex) {
                // Don't let App Insights interfere with presenting the dialog.
                $log.error(ex);
            }

            var titleText = 'Unspecified error.';
            var messages = [];
            if (errorText) {
                titleText = errorText;
            }
            if (responseText) {
                messages.push(responseText);
            }

            return messageList(titleText, messages);
        }

        /**
         * Show a dialog.
         * The controller specified should request 'DialogInstance' in its constructor, this is an object used
         * to close/dismiss the dialog.
         * The DialogInstance object has two functions:
         *   ok({object}) - The provided object will be resolved in the promise returned.
         *   cancel()
         *
         * @param {object} options - An options object with the following properties:
         *   {string|function} controller - String specifying registered controller, or controller function.
         *   {string} controllerAs - Optional.
         *   {string} templateUrl - Specify this, *or* template.
         *   {string} template - Specify this, *or* templateUrl.
         *   {string} size - Optional. Refer DialogService.sizes
         *   {object} locals - Objects to be injected into the controller. Optional.
         *   {object} scope - An Angular scope to use for the dialog controller.
         *   e.g.
         *   {
         *     someData: [1, 2, 3],
         *     style: 'blah blah blah'
         *   }
         *
         * @returns {promise} Resolved if the dialog is closed by 'ok', rejected otherwise.
         *   Resolve may include an object provided by the dialog controller.
         */
        function show(options) {

            /* Validate Input */

            if (!angular.isFunction(options.controller) && !angular.isString(options.controller)) {
                throw 'Dialog show controller must be a string or function';
            }
            if (!angular.isString(options.templateUrl) && !angular.isString(options.template)) {
                throw 'Dialog show templateUrl or template must be a string';
            }


            var resolve = {};

            if (!_.isNil(options.locals)) {
                // Copy definition of 'locals' into format usable by uibModal 'resolve'
                for (var prop in options.locals) {
                    if (options.locals.hasOwnProperty(prop)) {

                        // Closure for local variable
                        /* jshint loopfunc: true */
                        (function (prop) {
                            resolve[prop] = function () { return options.locals[prop]; };
                        })(prop);
                    }
                }
            }

            // For injecting 'DialogInstance' into dialog controller
            var dialogInstance = {
                _instance: null,
                ok: function (result) {
                    if (dialogInstance._instance === null) {
                        throw 'Cannot close dialog, dialog instance is null';
                    }

                    dialogInstance._instance.close(result);
                },
                cancel: function () {
                    if (dialogInstance._instance === null) {
                        throw 'Cannot close dialog, dialog instance is null';
                    }

                    dialogInstance._instance.dismiss();
                }
            };

            resolve.DialogInstance = function () { return dialogInstance; };

            // A fake scope to prevent backdrop click from closing the modal.
            // This depends on the inner workings of the UI $modal.open function, which could change in a future version of UIB,
            // SO WATCH OUT!
            // Note the call to providedScope.$new() in UIB:
            // https://github.com/angular-ui/bootstrap/blob/1653afa210b9372fa342a87cb803850681e288b8/src/modal/modal.js#L729
            var fakeScope = {
                $new: function () {
                    var newScope;
                    if (!_.isNil(options.scope)) {
                        newScope = options.scope.$new();
                    } else {
                        newScope = $rootScope.$new();
                    }

                    newScope.$on('modal.closing', function (event, message) {
                        if (message === 'backdrop click') {
                            event.preventDefault();
                        }
                    });

                    return newScope;
                }
            };

            var uibModalOptions = {
                resolve: resolve,
                controller: options.controller,
                controllerAs: options.controllerAs,
                scope: fakeScope,
                windowTemplateUrl: 'app/Controls/Dialog/dialog-template-window.tmpl.html'
            };

            if (angular.isString(options.size)) {
                uibModalOptions.size = options.size;
            }

            if (angular.isString(options.templateUrl)) {
                uibModalOptions.templateUrl = options.templateUrl;
            } else {
                uibModalOptions.template = options.template;
            }

            var modalInstance = $uibModal.open(uibModalOptions);

            dialogInstance._instance = modalInstance;

            return modalInstance.result.then(
                null,
                function cancel(result) {
                    if (result === 'backdrop click' || result === 'escape key press') {
                        // Swallow legitimate close results, don't want to pass them on. An empty rejection is preferred.
                        return $q.reject();
                    } else {
                        return $q.reject(result);
                    }
                });
        }


        /**
         * Show an Angular UI Bootstrap 'modal' dialog using the default template and controller.
         *
         * @param {string} okText - The text for the 'OK' button.
         * @param {string} cancelText - The text for the 'Cancel' button, provide an empty string to hide the button.
         * @param {string} title - The text for the title of the dialog, provide an empty string to hide the title.
         * @param {string} body - Body for the dialog, in HTML.
         *
         * @returns {promise} Resolved if the dialog is closed by 'ok', rejected otherwise.
         */
        function showDefaultUibDialog(okText, cancelText, title, body) {
            return show({
                controller: 'DialogDefaultCtrl',
                controllerAs: 'vm',
                templateUrl: 'app/Controls/Dialog/dialog-default.tmpl.html',
                locals: {
                    okText: okText,
                    cancelText: cancelText,
                    title: title,
                    body: body
                }
            });
        }
    }
})();